import {
    EpisodeStatus,
    VisitDatetimeField,
    VisitOutcome,
    VisitState,
} from '@axle-health/graphql-schema';

import { HH_CONTAINER_KEYS } from '../containerKeys';
import { addCalendarDays, addDays, date, dateInDays, datetime } from '../graphInputHelpers';
import { FilterInputByEntity, QueueDefinition, RootEntity } from '../types';
import { DEFAULT_CONTAINER_FILTER_WINDOW_DAYS } from './pinnacleServiceIds';

/**
 * The home health demo org. Its queues are keyed on service name rather than
 * service external ID, because a demo org's services carry no external IDs.
 *
 * Every queue is declared in full here, including the ones whose filters happen to
 * match another org's. A queue is per-org configuration, so changing what this org
 * counts means editing this file and no other.
 */

const OVERDUE_GRACE_MS = 15 * 60 * 1000;

/** Recert and reassessment services that satisfy a reassessment. */
const REASSESSMENT_SERVICE_NAMES = [
    'OT Reassessment',
    'OT Recert',
    'OT ROC/Recert',
    'PT Reassessment',
    'PT Recert',
    'PT ROC/Recert',
    'SN Recert',
    'SN ROC/Recert',
    'ST Reassessment',
    'ST Recert',
    'ST ROC/Recert',
];

/**
 * Visits whose effective date falls in a calendar-date range.
 *
 * A visit's effective date is whichever of its dates it actually has: a scheduled
 * start, else a tentative date, else the window it is planned in. So the range has
 * to be expressed against all three rather than one field. The start-datetime arm
 * takes an exclusive end because a range on a timestamp otherwise stops at midnight
 * opening the last day and drops that day's visits.
 */
const effectiveDateInRange = (
    startDate: string,
    endDate: string,
): FilterInputByEntity[RootEntity.VISIT] => ({
    startDatetime: {
        range: { start: startDate, end: addCalendarDays(endDate, 1) },
    },
    OR: {
        startDatetime: { isNull: true },
        tentativeDate: {
            range: { start: startDate, end: endDate },
        },
        OR: {
            startDatetime: { isNull: true },
            tentativeDate: { isNull: true },
            scheduledOrPlannedInDateWindow: { startDate, endDate },
        },
    },
});

// Active patients holding a pending visit in a discipline their care team does not
// cover.
const INCOMPLETE_CARE_TEAM: QueueDefinition<RootEntity.PATIENT> = {
    key: HH_CONTAINER_KEYS.incompleteCareTeam,
    label: 'Incomplete Care Team',
    rootEntity: RootEntity.PATIENT,
    snoozable: false,
    heavyQuery: false,
    buildFilters: () => ({
        isActive: { exact: true },
        hasEpisodes: {
            isActiveByDate: { exact: true },
            isActiveField: { exact: true },
            hasVisits: {
                disciplineCoveredByCareTeam: { exact: false },
                outcome: { exact: VisitOutcome.NONE },
            },
        },
    }),
};

// Start of care visits still missing a service, clinician or date.
const SOC: QueueDefinition<RootEntity.VISIT> = {
    key: HH_CONTAINER_KEYS.soc,
    label: 'SOCs',
    rootEntity: RootEntity.VISIT,
    snoozable: false,
    heavyQuery: false,
    buildFilters: () => ({
        services: { name: { iContains: 'soc' } },
        needsAssignment: { exact: true },
    }),
};

// Resumption of care visits still missing a service, clinician or date.
const ROC: QueueDefinition<RootEntity.VISIT> = {
    key: HH_CONTAINER_KEYS.roc,
    label: 'ROCs',
    rootEntity: RootEntity.VISIT,
    snoozable: false,
    heavyQuery: false,
    buildFilters: () => ({
        services: { name: { iContains: 'roc' } },
        needsAssignment: { exact: true },
    }),
};

// Eval visits still missing a service, clinician or date.
const EVAL: QueueDefinition<RootEntity.VISIT> = {
    key: HH_CONTAINER_KEYS.eval,
    label: 'Evals',
    rootEntity: RootEntity.VISIT,
    snoozable: false,
    heavyQuery: false,
    buildFilters: () => ({
        services: { name: { iContains: 'eval' } },
        needsAssignment: { exact: true },
    }),
};

// Unassigned visits whose fulfillment window closes within three days, or closed
// within the window looked back over.
const EXPIRING_FULFILLMENT_WINDOW: QueueDefinition<RootEntity.VISIT> = {
    key: HH_CONTAINER_KEYS.expiringFulfillmentWindow,
    label: 'Expiring Fulfillment Windows',
    rootEntity: RootEntity.VISIT,
    snoozable: false,
    heavyQuery: true,
    buildFilters: ({ now, timezone }) => ({
        patient: { isActive: { exact: true } },
        needsAssignment: { exact: true },
        fulfillmentWindowEndDate: {
            gte: dateInDays(now, timezone, -DEFAULT_CONTAINER_FILTER_WINDOW_DAYS),
            lte: dateInDays(now, timezone, 3),
        },
    }),
};

// Episodes holding a visit that still needs a service, clinician or date, reaching
// two weeks back so a just-ended episode still surfaces its unscheduled work.
const PATIENTS_NEEDING_SCHEDULING: QueueDefinition<RootEntity.EPISODE_OF_CARE> = {
    key: HH_CONTAINER_KEYS.patientsNeedingScheduling,
    label: 'Patients Needing Scheduling',
    rootEntity: RootEntity.EPISODE_OF_CARE,
    snoozable: false,
    heavyQuery: false,
    buildFilters: ({ now, timezone }) => ({
        patient: { isActive: { exact: true } },
        endDate: { gte: dateInDays(now, timezone, -14) },
        hasVisits: { needsAssignment: { exact: true } },
    }),
};

// Visits held in review because the patient did not show.
const PATIENT_NO_SHOWS: QueueDefinition<RootEntity.VISIT> = {
    key: HH_CONTAINER_KEYS.patientNoShows,
    label: 'Patient No-Shows',
    rootEntity: RootEntity.VISIT,
    snoozable: false,
    heavyQuery: true,
    buildFilters: ({ now }) => ({
        patient: { isActive: { exact: true } },
        startDatetime: {
            gte: datetime(addDays(now, -DEFAULT_CONTAINER_FILTER_WINDOW_DAYS)),
        },
        state: { exact: VisitState.IN_REVIEW },
        outcome: { exact: VisitOutcome.PATIENT_DID_NOT_SHOW },
    }),
};

// Episodes ending within ten days that hold neither a recert nor a discharge visit.
const PENDING_END_OF_EPISODE: QueueDefinition<RootEntity.EPISODE_OF_CARE> = {
    key: HH_CONTAINER_KEYS.pendingEndOfEpisode,
    label: 'Pending End of Episode',
    rootEntity: RootEntity.EPISODE_OF_CARE,
    snoozable: false,
    heavyQuery: false,
    buildFilters: ({ now, timezone }) => ({
        patient: { isActive: { exact: true } },
        endDate: {
            range: {
                start: date(now, timezone),
                end: dateInDays(now, timezone, 10),
            },
        },
        visits: {
            NOT: {
                services: { name: { iContains: 'recert' } },
                outcome: { inList: [VisitOutcome.NONE, VisitOutcome.COMPLETED] },
                OR: {
                    services: { name: { iContains: 'discharge' } },
                    outcome: { inList: [VisitOutcome.NONE, VisitOutcome.COMPLETED] },
                },
            },
        },
    }),
};

// Patients on hold who still hold upcoming visits.
const TRANSFERS: QueueDefinition<RootEntity.PATIENT> = {
    key: HH_CONTAINER_KEYS.transfers,
    label: 'Transfers',
    rootEntity: RootEntity.PATIENT,
    snoozable: false,
    heavyQuery: false,
    buildFilters: ({ now, timezone }) => ({
        isActive: { exact: true },
        hasEpisodes: { status: { exact: EpisodeStatus.ON_HOLD } },
        hasVisits: {
            outcome: { exact: VisitOutcome.NONE },
            AND: effectiveDateInRange(
                date(now, timezone),
                dateInDays(now, timezone, DEFAULT_CONTAINER_FILTER_WINDOW_DAYS),
            ),
        },
    }),
};

// Discharged patients who still hold upcoming visits.
const DISCHARGE_WITH_PENDING_VISITS: QueueDefinition<RootEntity.PATIENT> = {
    key: HH_CONTAINER_KEYS.dischargeWithPendingVisits,
    label: 'Discharge with Pending Visits',
    rootEntity: RootEntity.PATIENT,
    snoozable: false,
    heavyQuery: true,
    buildFilters: ({ now, timezone }) => ({
        isActive: { exact: false },
        hasVisits: {
            outcome: { exact: VisitOutcome.NONE },
            AND: effectiveDateInRange(
                date(now, timezone),
                dateInDays(now, timezone, DEFAULT_CONTAINER_FILTER_WINDOW_DAYS),
            ),
        },
    }),
};

// Pending visits assigned to a clinician who is no longer active.
const INACTIVE_CLINICIAN_VISITS: QueueDefinition<RootEntity.VISIT> = {
    key: HH_CONTAINER_KEYS.inactiveClinicianVisits,
    label: 'Inactive Clinicians',
    rootEntity: RootEntity.VISIT,
    snoozable: false,
    heavyQuery: true,
    buildFilters: ({ now, timezone }) => ({
        outcome: { exact: VisitOutcome.NONE },
        assignedClinicians: { isActive: { exact: false } },
        effectiveDate: {
            gte: dateInDays(now, timezone, -DEFAULT_CONTAINER_FILTER_WINDOW_DAYS),
        },
    }),
};

// Active patients with nothing upcoming on the books.
const NO_FUTURE_APPOINTMENTS: QueueDefinition<RootEntity.PATIENT> = {
    key: HH_CONTAINER_KEYS.noFutureAppointments,
    label: 'No Future Appointments',
    rootEntity: RootEntity.PATIENT,
    snoozable: false,
    heavyQuery: true,
    buildFilters: ({ now, timezone }) => ({
        isActive: { exact: true },
        NOT: {
            hasVisits: {
                outcome: { exact: VisitOutcome.NONE },
                AND: effectiveDateInRange(
                    date(now, timezone),
                    dateInDays(now, timezone, DEFAULT_CONTAINER_FILTER_WINDOW_DAYS),
                ),
            },
        },
    }),
};

// Completed SOC, ROC and eval visits with no order behind them to bill against.
const COMPLETED_VISITS_NOT_MAPPED_TO_ORDERS: QueueDefinition<RootEntity.VISIT> = {
    key: HH_CONTAINER_KEYS.completedVisitsNotMappedToOrders,
    label: 'Completed SOC/ROC/Evals Not Mapped to Orders',
    rootEntity: RootEntity.VISIT,
    snoozable: false,
    heavyQuery: true,
    buildFilters: () => ({
        outcome: { exact: VisitOutcome.COMPLETED },
        hasOrder: { exact: false },
        services: {
            name: { iContains: 'soc' },
            OR: {
                name: { iContains: 'roc' },
                OR: {
                    name: { iContains: 'eval' },
                },
            },
        },
    }),
};

// Evals sitting ahead of the same patient's upcoming start of care.
const EVALS_BEFORE_SOCS: QueueDefinition<RootEntity.VISIT> = {
    key: HH_CONTAINER_KEYS.evalsBeforeSocs,
    label: 'Evals Before SOCs',
    rootEntity: RootEntity.VISIT,
    snoozable: false,
    heavyQuery: false,
    buildFilters: ({ now, timezone }) => ({
        services: { name: { iContains: 'eval' } },
        outcome: { inList: [VisitOutcome.NONE, VisitOutcome.COMPLETED] },
        relatedVisitCondition: {
            exists: true,
            filter: {
                services: { name: { iContains: 'soc' } },
                outcome: { exact: VisitOutcome.NONE },
                effectiveDate: { gte: date(now, timezone) },
            },
            timeConstraints: [
                {
                    field: VisitDatetimeField.EFFECTIVE_DATE,
                    relativeTo: VisitDatetimeField.EFFECTIVE_DATE,
                    gtOffsetMinutes: 0,
                },
            ],
            samePatient: true,
        },
    }),
};

// Active episodes carrying more than one pending agency discharge, or more than one
// pending recert. Only one of each belongs on an episode.
const MULTIPLE_OASIS_PER_EPISODE: QueueDefinition<RootEntity.EPISODE_OF_CARE> = {
    key: HH_CONTAINER_KEYS.multipleOasisPerEpisode,
    label: 'Multiple OASIS Discharge/Recert per Episode',
    rootEntity: RootEntity.EPISODE_OF_CARE,
    snoozable: false,
    heavyQuery: false,
    buildFilters: () => ({
        isActiveByDate: { exact: true },
        isActiveField: { exact: true },
        hasVisits: {
            services: { name: { iContains: 'agency discharge' } },
            outcome: { exact: VisitOutcome.NONE },
            relatedVisitCondition: {
                exists: true,
                filter: {
                    services: { name: { iContains: 'agency discharge' } },
                    outcome: { exact: VisitOutcome.NONE },
                },
                sameEpisode: true,
            },
        },
        OR: {
            hasVisits: {
                services: { name: { iContains: 'recert' } },
                outcome: { exact: VisitOutcome.NONE },
                relatedVisitCondition: {
                    exists: true,
                    filter: {
                        services: { name: { iContains: 'recert' } },
                        outcome: { exact: VisitOutcome.NONE },
                    },
                    sameEpisode: true,
                },
            },
        },
    }),
};

// Active patients who have had a reassessment service at some point.
//
// The queue is meant to be narrower than that: patients whose last reassessment was
// 25 or more days ago with no further one booked in the next five. Both halves need
// an aggregate over a filtered subset of a patient's visits, which the filter inputs
// cannot express, so the queue counts the wider set until they can.
const PENDING_REASSESSMENT: QueueDefinition<RootEntity.PATIENT> = {
    key: HH_CONTAINER_KEYS.pendingReassessment,
    label: 'Pending Reassessment',
    rootEntity: RootEntity.PATIENT,
    snoozable: false,
    heavyQuery: false,
    buildFilters: () => ({
        isActive: { exact: true },
        hasVisits: {
            services: { name: { inList: REASSESSMENT_SERVICE_NAMES } },
        },
    }),
};

// Scheduled or confirmed visits whose start time has passed by more than the grace
// period, so nobody has started them.
const OVERDUE_VISITS: QueueDefinition<RootEntity.VISIT> = {
    key: HH_CONTAINER_KEYS.overdueVisits,
    label: 'Overdue',
    rootEntity: RootEntity.VISIT,
    snoozable: false,
    heavyQuery: false,
    buildFilters: ({ now }) => ({
        state: { inList: [VisitState.SCHEDULED, VisitState.CONFIRMED] },
        startDatetime: { lt: datetime(new Date(now.getTime() - OVERDUE_GRACE_MS)) },
    }),
};

export const HOME_HEALTH_QUEUES: QueueDefinition[] = [
    INCOMPLETE_CARE_TEAM,
    SOC,
    ROC,
    EVAL,
    EXPIRING_FULFILLMENT_WINDOW,
    PATIENTS_NEEDING_SCHEDULING,
    OVERDUE_VISITS,
    PATIENT_NO_SHOWS,
    PENDING_END_OF_EPISODE,
    TRANSFERS,
    DISCHARGE_WITH_PENDING_VISITS,
    INACTIVE_CLINICIAN_VISITS,
    NO_FUTURE_APPOINTMENTS,
    COMPLETED_VISITS_NOT_MAPPED_TO_ORDERS,
    EVALS_BEFORE_SOCS,
    MULTIPLE_OASIS_PER_EPISODE,
    PENDING_REASSESSMENT,
];
